import { Facebook, Twitter, Linkedin, Link2, Mail, MessageCircle, Send, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

interface SocialShareProps {
  url?: string;
  title?: string;
  description?: string;
  className?: string;
  compact?: boolean;
}

const SocialShare = ({
  url = typeof window !== "undefined" ? window.location.href : "",
  title = "RentSense NL – Understand Your Dutch Rental Contract",
  description = "Identify risky clauses in your Dutch rental contract before you sign.",
  className = "",
  compact = false,
}: SocialShareProps) => {
  const { toast } = useToast();

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
    reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%20${encodedUrl}`,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied!",
        description: "The link has been copied to your clipboard.",
      });
    } catch {
      toast({
        title: "Failed to copy",
        description: "Please copy the link manually.",
        variant: "destructive",
      });
    }
  };

  const openShare = (shareUrl: string) => {
    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description,
          url,
        });
      } catch (err) {
        // User cancelled or share failed silently
      }
    }
  };

  const supportsNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  const allButtons = [
    { key: "whatsapp", icon: MessageCircle, label: "Share on WhatsApp", action: () => openShare(shareLinks.whatsapp) },
    { key: "telegram", icon: Send, label: "Share on Telegram", action: () => openShare(shareLinks.telegram) },
    { key: "facebook", icon: Facebook, label: "Share on Facebook", action: () => openShare(shareLinks.facebook) },
    { key: "twitter", icon: Twitter, label: "Share on Twitter", action: () => openShare(shareLinks.twitter) },
    { key: "linkedin", icon: Linkedin, label: "Share on LinkedIn", action: () => openShare(shareLinks.linkedin) },
    { key: "email", icon: Mail, label: "Share via Email", action: () => window.location.href = shareLinks.email },
    { key: "copy", icon: Link2, label: "Copy link", action: copyToClipboard },
  ];

  // In compact mode, show fewer buttons
  const buttons = compact ? allButtons.slice(0, 4) : allButtons;

  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      {supportsNativeShare && (
        <Button
          variant="outline"
          size="icon"
          onClick={handleNativeShare}
          aria-label="Share"
          className="bg-primary/10 border-primary/20 hover:bg-primary/20"
        >
          <Share2 className="w-4 h-4" />
        </Button>
      )}
      {buttons.map(({ key, icon: Icon, label, action }) => (
        <Button
          key={key}
          variant="outline"
          size="icon"
          onClick={action}
          aria-label={label}
        >
          <Icon className="w-4 h-4" />
        </Button>
      ))}
    </div>
  );
};

export default SocialShare;
